import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';

/**
 * Telemetry — widgets animés portés du proto V2 (Telemetry.jsx).
 *
 * Tous respectent DESIGN.md §motion : easing ease-out, plafond 400ms sur
 * animations critiques, pas de bounces, pas de scale > 1.02. Le hook
 * `prefers-reduced-motion` est honoré via @media query globale (globals.css).
 */

const easeOutCubic = (p: number): number => 1 - Math.pow(1 - p, 3);

function useAnimated(target: number, duration = 900): number {
  const [v, setV] = useState(target);
  useEffect(() => {
    let raf = 0;
    const t0 = performance.now();
    const startV = v;
    const tick = (t: number): void => {
      const p = Math.min(1, (t - t0) / duration);
      setV(startV + (target - startV) * easeOutCubic(p));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration]);
  return v;
}

// ─── AnimatedNumber ────────────────────────────────────────────────────────

export function AnimatedNumber({
  value,
  format = (v) => String(Math.round(v)),
  className = '',
  style,
  duration = 900,
}: {
  value: number;
  format?: (v: number) => string;
  className?: string;
  style?: CSSProperties;
  duration?: number;
}): JSX.Element {
  const animated = useAnimated(value, duration);
  return (
    <span className={`tabular-nums ${className}`} style={style}>
      {format(animated)}
    </span>
  );
}

// ─── Sparkline ─────────────────────────────────────────────────────────────

export function Sparkline({
  data,
  color = 'var(--acid-500)',
  width = 120,
  height = 32,
  fill = true,
  strokeWidth = 1.5,
  animate = true,
}: {
  data: number[];
  color?: string;
  width?: number;
  height?: number;
  fill?: boolean;
  strokeWidth?: number;
  /**
   * Anime le tracé en stroke-draw progressif (proto V2 `tcc-draw`). Désactivable
   * pour les contextes où l'apparition instantanée est préférable.
   */
  animate?: boolean;
}): JSX.Element | null {
  if (data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const xStep = width / (data.length - 1);
  const pad = 3;
  const innerH = height - pad * 2;
  const pts = data.map<[number, number]>((v, i) => [
    i * xStep,
    pad + innerH - ((v - min) / range) * innerH,
  ]);
  const d = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const lastPoint = pts[pts.length - 1];
  if (!lastPoint) return null;
  const [lastX, lastY] = lastPoint;
  // Le proto fige `stroke-dasharray: 1000` indépendamment de la vraie longueur
  // du path — couvre confortablement les sparklines typiques (< 600px de path).
  const strokeStyle = animate
    ? ({ strokeDasharray: 1000, strokeDashoffset: 1000 } as CSSProperties)
    : undefined;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="block">
      {fill && (
        <path
          d={`${d} L${lastX} ${height} L0 ${height} Z`}
          fill={color}
          fillOpacity={0.12}
        />
      )}
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={animate ? 'animate-draw' : undefined}
        style={strokeStyle}
      />
      <circle cx={lastX} cy={lastY} r={2.5} fill={color} />
    </svg>
  );
}

// ─── ProgressRing ──────────────────────────────────────────────────────────

export function ProgressRing({
  value,
  size = 64,
  thickness = 6,
  color = 'var(--volt-500)',
  trackColor = 'var(--ink-700)',
  label,
}: {
  value: number; // 0..100
  size?: number;
  thickness?: number;
  color?: string;
  trackColor?: string;
  label?: string;
}): JSX.Element {
  const animated = useAnimated(value, 900);
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, animated)) / 100) * c;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke={trackColor} strokeWidth={thickness} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={color}
          strokeWidth={thickness}
          fill="none"
          strokeDasharray={`${dash} ${c}`}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 200ms ease-out' }}
        />
      </svg>
      <span className="absolute font-mono text-sm font-medium tabular-nums" style={{ color: 'var(--paper-50)' }}>
        {label ?? `${Math.round(animated)}%`}
      </span>
    </div>
  );
}

// ─── PulseDot ──────────────────────────────────────────────────────────────
//
// Proto V2 = core statique + ring qui s'étale (`tcc-pulse-ring` scale 1 → 2.6,
// opacity 0.55 → 0, 1800ms infinite). Pour reproduire fidèlement il faut deux
// éléments séparés : sinon le core grossit aussi, ce qui n'est pas voulu.

export function PulseDot({
  color = 'var(--acid-500)',
  size = 8,
}: {
  color?: string;
  size?: number;
}): JSX.Element {
  return (
    <span
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <span
        className="absolute inset-0 rounded-full animate-pulse-ring"
        style={{ backgroundColor: color }}
      />
      <span
        className="relative rounded-full"
        style={{ width: size, height: size, backgroundColor: color }}
      />
    </span>
  );
}

// ─── SparkleBurst ──────────────────────────────────────────────────────────
//
// Particule de validation — 5 instances qui partent dans 5 directions aléatoires
// (`--sx`/`--sy` custom). Proto V2 `tcc-sparkle-burst` 900ms. Utilisé sur la
// validation d'une mission step pour signaler "+N pts trouvés" en accompagnement
// du XpFloater. À placer dans un parent `relative`. Re-trigger via `tokenKey`.

const SPARKLE_OFFSETS: Array<{ sx: number; sy: number }> = [
  { sx: -28, sy: -22 },
  { sx: 30, sy: -18 },
  { sx: -34, sy: 14 },
  { sx: 26, sy: 24 },
  { sx: 0, sy: -36 },
];

export function SparkleBurst({
  color = 'var(--volt-500)',
  tokenKey,
  className = '',
}: {
  color?: string;
  /** Change ce token pour rejouer l'animation (typiquement Date.now()). */
  tokenKey: number | string;
  className?: string;
}): JSX.Element {
  return (
    <span key={tokenKey} className={`pointer-events-none absolute inset-0 ${className}`} aria-hidden="true">
      {SPARKLE_OFFSETS.map((offset, i) => (
        <span
          key={i}
          className="absolute left-7 top-1/2 size-[5px] rounded-full animate-sparkle-burst"
          style={
            {
              '--sx': `${offset.sx}px`,
              '--sy': `${offset.sy}px`,
              backgroundColor: color,
              boxShadow: `0 0 8px 2px ${color}8c`,
            } as CSSProperties
          }
        />
      ))}
    </span>
  );
}

// ─── XpFloater ─────────────────────────────────────────────────────────────
//
// Pop-up `+N pts` animé qui flotte vers le haut puis fade en 1.4s.
// Port du proto `tcc-xp-floater` (cf. docs/XP_BAREME.md). À placer dans un
// parent `relative`. Re-trigger : changer `tokenKey` (e.g. Date.now()) → React
// remount le composant → l'animation se re-rejoue depuis le début.
//
// MVP2.5 T7 — option `caption` : phrase explicative affichée sous le chiffre
// pendant la durée de l'animation (1.4s) pour répondre à « pourquoi je gagne
// ces points ? ». Ex: « Puzzle résolu », « Coup d'ouverture trouvé ». Pas de
// nouveau timer JS — la caption hérite de l'animation parent (xp-float).

export function XpFloater({
  value,
  tokenKey,
  caption,
  className = '',
}: {
  value: number;
  /** Change ce token pour re-déclencher l'animation (typiquement Date.now()). */
  tokenKey: number | string;
  /**
   * Phrase explicative affichée sous le chiffre (MVP2.5 T7). Tutoiement,
   * 2-4 mots max pour rester lisible pendant 1.4s d'animation.
   */
  caption?: string;
  className?: string;
}): JSX.Element | null {
  if (value <= 0) return null;
  return (
    <span
      key={tokenKey}
      aria-hidden="true"
      className={`absolute pointer-events-none whitespace-nowrap z-20 animate-xp-float flex flex-col items-center ${className}`}
    >
      <span
        className="font-display font-bold text-volt-500 text-sm"
        style={{ textShadow: '0 0 8px rgba(91,233,185,0.7)' }}
      >
        +{value} pts
      </span>
      {caption && (
        <span className="font-mono text-[10px] text-volt-500/80 uppercase tracking-wider mt-0.5">
          {caption}
        </span>
      )}
    </span>
  );
}

// ─── Heatmap ───────────────────────────────────────────────────────────────

export function Heatmap({
  data,
  cell = 10,
  gap = 3,
  weeks = 13,
  color = 'var(--volt-500)',
  ariaLabel,
}: {
  data: number[]; // 0..4 per day, ordered oldest → newest
  cell?: number;
  gap?: number;
  weeks?: number;
  color?: string;
  ariaLabel?: string;
}): JSX.Element {
  // 7 jours × weeks colonnes (par défaut 13 = ~90j)
  const rows = 7;
  const cols = Math.min(weeks, Math.ceil(data.length / rows));
  const width = cols * (cell + gap);
  const height = rows * (cell + gap);
  const today = new Date();
  return (
    <div
      aria-label={ariaLabel ?? `Activité ${data.length} jours`}
      style={{ width, height }}
      className="relative"
    >
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {data.map((v, i) => {
          const col = Math.floor(i / rows);
          const row = i % rows;
          if (col >= cols) return null;
          const opacity = Math.max(0.08, Math.min(1, 0.08 + (v / 4) * 0.92));
          const date = new Date(today);
          date.setDate(today.getDate() - (data.length - 1 - i));
          return (
            <rect
              key={i}
              x={col * (cell + gap)}
              y={row * (cell + gap)}
              width={cell}
              height={cell}
              rx={2}
              fill={v === 0 ? 'var(--ink-700)' : color}
              fillOpacity={v === 0 ? 1 : opacity}
            >
              <title>{`${date.toISOString().slice(0, 10)} — ${v} ${v === 1 ? 'tentative' : 'tentatives'}`}</title>
            </rect>
          );
        })}
      </svg>
    </div>
  );
}

// ─── SessionTimer ──────────────────────────────────────────────────────────

export function SessionTimer({
  startedAtMs,
  className = '',
}: {
  startedAtMs: number;
  className?: string;
}): JSX.Element {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const elapsedSec = Math.floor((now - startedAtMs) / 1000);
  const mm = String(Math.floor(elapsedSec / 60)).padStart(2, '0');
  const ss = String(elapsedSec % 60).padStart(2, '0');
  return (
    <span className={`font-mono tabular-nums text-ink-300 text-sm ${className}`}>
      {mm}:{ss}
    </span>
  );
}

// ─── Spider (radar chart 6 axes) ───────────────────────────────────────────

export interface SpiderAxis {
  label: string;
  value: number; // 0..100
}

export function Spider({
  axes,
  size = 200,
  color = 'var(--acid-500)',
  bgColor = 'var(--ink-700)',
}: {
  axes: SpiderAxis[];
  size?: number;
  color?: string;
  bgColor?: string;
}): JSX.Element {
  const n = axes.length;
  const cx = size / 2;
  const cy = size / 2;
  const r = (size / 2) * 0.78;
  const angle = (i: number): number => (Math.PI * 2 * i) / n - Math.PI / 2;
  const point = (val: number, i: number): [number, number] => {
    const a = angle(i);
    const rr = (Math.max(0, Math.min(100, val)) / 100) * r;
    return [cx + Math.cos(a) * rr, cy + Math.sin(a) * rr];
  };
  const polygon = axes.map((axis, i) => point(axis.value, i).join(',')).join(' ');
  const outer = axes.map((_, i) => point(100, i).join(',')).join(' ');
  const rings = [25, 50, 75, 100];
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-label="Profil joueur">
      {rings.map((pct) => (
        <polygon
          key={pct}
          points={axes.map((_, i) => point(pct, i).join(',')).join(' ')}
          fill="none"
          stroke={bgColor}
          strokeOpacity={0.4}
          strokeWidth={1}
        />
      ))}
      {axes.map((_, i) => {
        const [x, y] = point(100, i);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke={bgColor} strokeOpacity={0.4} strokeWidth={1} />;
      })}
      <polygon points={polygon} fill={color} fillOpacity={0.18} stroke={color} strokeWidth={1.5} strokeLinejoin="round" />
      <polygon points={outer} fill="none" stroke={bgColor} strokeWidth={1} />
      {axes.map((axis, i) => {
        const [x, y] = point(115, i);
        return (
          <text
            key={i}
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={10}
            fill="var(--ink-300)"
            style={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'var(--font-mono)' }}
          >
            {axis.label}
          </text>
        );
      })}
    </svg>
  );
}

// ─── EvalBar ───────────────────────────────────────────────────────────────
//
// Barre verticale exprimant l'évaluation Stockfish d'une position.
// `cp` = centipawns du point de vue Blancs. Mate → ±10 000.

export function EvalBar({
  cp,
  height = 320,
  width = 18,
}: {
  cp: number;
  height?: number;
  width?: number;
}): JSX.Element {
  // Clamp visuel : ±500cp = barre quasi pleine, courbe sigmoïde pour le mid.
  const clamped = Math.max(-10000, Math.min(10000, cp));
  const norm = (() => {
    if (clamped >= 9000) return 1;
    if (clamped <= -9000) return 0;
    return 0.5 + 0.5 * Math.tanh(clamped / 350);
  })();
  const whiteH = Math.round(height * norm);
  return (
    <div className="relative inline-block bg-ink-700 rounded overflow-hidden" style={{ width, height }}>
      <div className="absolute bottom-0 left-0 right-0 bg-paper-50" style={{ height: whiteH, transition: 'height 200ms ease-out' }} />
      <div
        className="absolute left-0 right-0 h-px bg-acid-500/60"
        style={{ bottom: '50%' }}
      />
      <span
        className="absolute left-1/2 -translate-x-1/2 font-mono text-[10px] tabular-nums px-1 rounded bg-ink-900/80 text-paper-50"
        style={{ bottom: norm > 0.5 ? 6 : 'auto', top: norm <= 0.5 ? 6 : 'auto' }}
      >
        {Math.abs(cp) >= 9000 ? `${cp >= 0 ? '+' : '-'}M` : (cp / 100).toFixed(1)}
      </span>
    </div>
  );
}

// ─── MoveTicker ────────────────────────────────────────────────────────────
//
// Liste défilante de coups joués. Tabular numérique pour les indices ply.

export function MoveTicker({
  moves,
  currentPly,
}: {
  moves: string[];
  currentPly: number;
}): JSX.Element {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 max-h-32 overflow-y-auto px-2 py-1 bg-ink-900 border border-ink-700 rounded font-mono text-xs tabular-nums">
      {moves.map((m, i) => {
        const ply = i + 1;
        const turnNo = Math.floor(i / 2) + 1;
        const isWhite = i % 2 === 0;
        const isCurrent = ply === currentPly;
        return (
          <span key={i} className={isCurrent ? 'text-acid-500 font-semibold' : 'text-ink-300'}>
            {isWhite && <span className="text-ink-300/60 mr-1">{turnNo}.</span>}
            {m}
          </span>
        );
      })}
    </div>
  );
}
